import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Split, Loader2, ShieldAlert, ArrowLeft } from "lucide-react";
import PasswordStrengthMeter from "@/components/PasswordStrengthMeter";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset — please log in with your new password.");
      navigate("/login");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset your password. Please request a new link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-foreground text-background p-12 grain-bg">
        <Link to="/" className="flex items-center gap-2" data-testid="reset-logo">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <Split className="h-5 w-5" />
          </div>
          <span className="font-heading text-xl" style={{ fontWeight: 900 }}>BogoShare</span>
        </Link>
        <div>
          <h2 className="font-heading text-4xl leading-tight" style={{ fontWeight: 900 }}>
            Choose a<br /><span className="text-accent">new password.</span>
          </h2>
          <p className="mt-4 text-background/70 max-w-sm leading-relaxed">
            Pick something strong you'll remember. You'll be logged out of other sessions for safety.
          </p>
        </div>
        <p className="text-xs font-mono-code text-background/50">Secure code-exchange settlement</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Split className="h-5 w-5" />
            </div>
            <span className="font-heading text-xl" style={{ fontWeight: 900 }}>BogoShare</span>
          </div>

          <h1 className="font-heading text-3xl tracking-tight" style={{ fontWeight: 700 }}>Set a new password</h1>

          {!token ? (
            <div data-testid="reset-invalid" className="mt-4 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <span>This reset link is missing its token. Please request a new link.</span>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              {error && (
                <div data-testid="reset-error" role="alert" className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <div>
                <Label htmlFor="password">New password</Label>
                <Input
                  id="password" type="password" data-testid="reset-password-input" value={password} required
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1"
                />
                <PasswordStrengthMeter password={password} />
              </div>
              <div>
                <Label htmlFor="confirm">Confirm new password</Label>
                <Input
                  id="confirm" type="password" data-testid="reset-confirm-input" value={confirm} required
                  onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className="mt-1"
                />
              </div>
              <Button
                type="submit" data-testid="reset-submit-btn" disabled={busy}
                className="w-full h-11 rounded-full hard-shadow-sm hover:-translate-y-0.5 transition-transform duration-200"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Reset password
              </Button>
            </form>
          )}

          <Link to="/login" data-testid="reset-back-link"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to log in
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
